import { Fragment, useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import download from 'downloadjs';
import AlertDialog from '../../shared/alertDialog';
import SurveyInfo from '../../shared/surveyInfo';
import SurveyReviewers from '../../shared/surveyReviewers';
import SurveyActionsDialog from '../../shared/surveyActionsDialog';
import AddReviewersDialog from '../../shared/addReviewersDialog';
import NotificationCard from '../../shared/notificationCard';
import ChooseNameDialog from '../../shared/chooseNameDialog';
import PageContainer from '../../shared/pageContainer';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import SurveyIconInfoTooltip from '../../shared/surveyIconInfoTooltip';
import CustomButton from '../../shared/customButton';
import ActionButton from '../../shared/actionButton';
import {
  API_ORG_CHART,
  api_survey,
  api_survey_export,
  api_survey_send_reminder,
  api_survey_reopen,
  api_survey_reviewers,
  api_survey_summary_share,
  api_survey_summary_report,
} from '../../../constants/apiRoutes';
import {
  CREATE_SURVEY_FIELD_NAMES,
  SURVEY_TITLE_FIELD,
  SURVEY_TYPES,
  getExpiryDialogFields,
  pruneAutoReminders,
  remountAutoReminders,
} from '../../../constants/survey';
import {
  SURVEY_STATUS_TYPES,
  USER_STATUSES,
} from '../../../constants/statuses';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { sticky } from '../../../constants/helperCssRules';
import { surveySelector } from '../../../store/selectors/surveySelector';
import { surveysSelector } from '../../../store/selectors/surveysSelector';
import {
  getSurvey,
  editSurvey,
  clearSurvey,
  getReviewers,
  clearReviewers,
} from '../../../store/modules/survey';
import {
  getAllUsers,
  clearAllUsers,
  getAllSurveyGroups,
  clearAllSurveyGroups,
} from '../../../store/modules/surveys';
import { showSuccessMessage, hasNextPage } from '../../../utility/uiUtils';
import http from '../../../utility/http';
import {
  isSameDate,
  getStartOfDay,
  addDaysFromSpecificHour,
  isDateInPast,
} from '../../../utility/dateUtils';
import {
  getUnionOfTwoArrays,
  isArrayEmpty,
  trimString,
  getObjectToNumberArray,
  getEmployees,
} from '../../../utility/helpers';
import {
  prepareSurveyTypes,
  prepareSurveyStatuses,
} from '../../../utility/survey';
import { prepareCycleReviewTypes } from '../../../utility/cycles';
import {
  hasAuthPermissionsCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import { useTranslations } from '../../../utility/useTranslations';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import {
  DEFAULT_PARAMS,
  STATUS,
  REVIEWER_ACTION_TYPES,
  prepareReviewersActions,
  hasAddReviewerButtonCheck,
  prepareUsersToNotify,
  prepareUsersForAction,
  prepareAllUsers,
} from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 'calc(100% - 260px)',
    maxWidth: 900,
  },
  surveyTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 'calc(100% - 162px)',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: spacing(4),
    flexShrink: 0,
  },
  actionButton: {
    marginLeft: spacing(2),
    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  linkButton: {
    marginLeft: spacing(2),
  },
  button: {
    borderRadius: 44,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '24px',
    padding: spacing(2, 4),
    maxHeight: 40,
  },
  seeReportButton: {
    boxSizing: 'border-box',
    backgroundColor: primary.blue1,
    border: 'none',
    color: primary.white,
    '&:hover': {
      backgroundColor: primary.blue1,
    },
  },
  anonymous: {
    display: 'block',
  },
  infoIcon: {
    flexShrink: 0,
    '& svg': {
      marginRight: 0,
    },
  },
  divider: {
    paddingTop: spacing(8),
    ...sticky(primary.white, 105),
  },
  cycleNotification: {
    backgroundColor: primary.bluish8,
    borderRadius: 8,
    display: 'flex',
    width: 'fit-content',
    padding: spacing(4),
    marginBottom: spacing(6),
  },
}));

const SurveySettingsPage = ({
  navigate,
  dispatch,
  auth,
  accessControl,
  organizationSettings,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SURVEY_SETTINGS);
  const { surveyId } = useParams();

  const { survey, reviewers } = useSelector(surveySelector);
  const { allUsers, allSurveyGroups } = useSelector(surveysSelector);

  const [isLoading, setIsLoading] = useState(true);
  const [isEditTitleOpened, setIsEditTitleOpened] = useState(false);
  const [organizationMentor, setOrganizationMentor] = useState({});
  const [currentPage, setCurrentPage] = useState(DEFAULT_PARAMS.page);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ [STATUS]: 0 });
  const [addReviewerDialogOpened, setAddReviewerDialogOpened] = useState(false);
  const [forceCompleteDialogOpened, setForceCompleteDialogOpened] =
    useState(false);
  const [sendReminderDialogOpened, setSendReminderDialogOpened] =
    useState(false);
  const [reopenSurveyDialogOpened, setReopenSurveyDialogOpened] =
    useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [reviewersToAdd, setReviewersToAdd] = useState([]);

  const isPartOfCycle = !!survey.cycle;
  const {
    createdFor,
    status,
    type,
    isAnonymous,
    title,
    scheduledFor,
    sentAt,
    expireAt,
    isOneTime,
  } = survey;
  const statuses = prepareSurveyStatuses(translations.statuses);
  const types = isPartOfCycle
    ? prepareCycleReviewTypes(translations.cycleReviewTypes)
    : prepareSurveyTypes(translations);
  const isScheduled = status === SURVEY_STATUS_TYPES.SCHEDULED;
  const isPeerReview = type === SURVEY_TYPES.PEER;
  const isSelfReview = type === SURVEY_TYPES.SELF;
  const isNps = type === SURVEY_TYPES.NPS;
  const isDraft =
    survey[CREATE_SURVEY_FIELD_NAMES.STATUS] === SURVEY_STATUS_TYPES.DRAFT;
  const hasGroups = !isArrayEmpty(allSurveyGroups);
  const employees = getEmployees(allUsers);

  const [
    canViewAllSurveys,
    canManageAllSurveys,
    canViewSurveysCreated,
    canManageSurveysCreated,
  ] = hasAuthPermissionsCheck(
    [
      PERMISSION_CODES.SURVEYS_VIEW_ALL,
      PERMISSION_CODES.SURVEYS_MANAGE_ALL,
      PERMISSION_CODES.SURVEYS_VIEW_CREATED,
      PERMISSION_CODES.SURVEYS_MANAGE_CREATED,
    ],
    accessControl
  );

  const goToProfilePage = userId => navigate(`/people/${userId}`);

  const canAccessUserProfileCheck = user =>
    isUserProfileAccessibleCheck(accessControl, auth.id, user.id);

  const fetchReviewers = useCallback(
    async (isLoadMore, page, statusFilter, searchTerm) => {
      const params = {
        page: isLoadMore ? page : DEFAULT_PARAMS.page,
        ...(statusFilter ? { [STATUS]: statusFilter } : {}),
        ...(searchTerm ? { search: searchTerm } : {}),
      };

      try {
        await getReviewers(dispatch, surveyId, isLoadMore, params);
      } finally {
        if (isLoadMore) {
          setIsFetchingNextPage(false);
        }
      }
    },
    [dispatch, surveyId]
  );

  const getOrganizationMentor = () =>
    http.get(API_ORG_CHART).then(({ data }) => ({
      ...data,
      last_name: translations.organization,
      isCeo: true,
    }));

  const getSurveyData = async () => {
    const { DEACTIVATED } = USER_STATUSES;
    const [surveyData, orgMentor] = await Promise.all([
      getSurvey(dispatch, surveyId),
      getOrganizationMentor(),
      ...(canViewAllSurveys ? [getAllSurveyGroups(dispatch)] : []),
    ]);

    const isCompleted = surveyData?.status === SURVEY_STATUS_TYPES.COMPLETED;
    setOrganizationMentor(orgMentor);

    if (!surveyData?.isAnonymous) {
      const isDraftSurvey = surveyData?.status === SURVEY_STATUS_TYPES.DRAFT;

      await getReviewers(dispatch, surveyId, false, {
        ...(isDraftSurvey ? { [PARAMS.EXCLUDE_USER_STATUS]: DEACTIVATED } : {}),
      });
    }

    if (!(surveyData?.isAnonymous && isCompleted)) {
      getAllUsers(dispatch);
    }
    setIsLoading(false);
  };

  const toggleSurveyTitleDialog = () => setIsEditTitleOpened(prev => !prev);

  const exportToCsv = () => {
    http
      .get(api_survey_export(survey.id), {
        responseType: 'arraybuffer',
      })
      .then(({ data }) => {
        download(
          new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }),
          `${survey.title}.xlsx`
        );
      });
  };

  const onChangeSurveyTitle = surveyTitle =>
    http.patch(api_survey(survey.id), { title: surveyTitle }).then(() => {
      showSuccessMessage(
        survey.cycle
          ? translations.renameReviewDialog.success
          : translations.renameSurveyDialog.success
      );
      toggleSurveyTitleDialog();
      getSurvey(dispatch, surveyId);
    });

  const toggleSendReminderDialog = () => {
    setSendReminderDialogOpened(!sendReminderDialogOpened);
    setActionType(
      !sendReminderDialogOpened ? REVIEWER_ACTION_TYPES.SEND_REMINDER : null
    );
  };

  const handleSendReminder = () => {
    if (isArrayEmpty(selectedUsers)) {
      const params = {
        client_url: `${window.location.origin}/fill-survey`,
        group_id: survey.id,
      };

      http.post(api_survey_send_reminder(survey.id), params).then(() => {
        showSuccessMessage(translations.sendReminderAllDialog.sendSuccess);
        setSendReminderDialogOpened(false);
        setActionType(null);
      });
    } else {
      http
        .post(
          api_survey_reviewers(survey.id),
          prepareUsersForAction(selectedUsers, actionType)
        )
        .then(() => {
          showSuccessMessage(translations.sendReminderDialog.success);
          setActionType(null);
          setSendReminderDialogOpened(false);
          setSelectedUsers([]);
        });
    }
  };

  const toggleReopenSurveyDialog = () => {
    setReopenSurveyDialogOpened(!reopenSurveyDialogOpened);
    setActionType(
      !reopenSurveyDialogOpened ? REVIEWER_ACTION_TYPES.REOPEN : null
    );
  };

  const handleReopenSurvey = async () => {
    await editSurvey(dispatch, survey.id, {
      [CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE]: addDaysFromSpecificHour(),
    });

    if (isArrayEmpty(selectedUsers)) {
      const params = {
        client_url: `${window.location.origin}/fill-survey`,
        group_id: survey.id,
      };

      http.post(api_survey_reopen(survey.id), params).then(async () => {
        showSuccessMessage(translations.surveyReopenAllDialog.success);
        setCurrentPage(DEFAULT_PARAMS.page);
        setReopenSurveyDialogOpened(false);
        setSelectedUsers([]);
        await getSurvey(dispatch, surveyId);
        await fetchReviewers(
          false,
          DEFAULT_PARAMS.page,
          filters[STATUS],
          search
        );
      });
    } else {
      http
        .post(
          api_survey_reviewers(survey.id),
          prepareUsersForAction(selectedUsers, actionType, survey.status)
        )
        .then(async () => {
          showSuccessMessage(translations.surveyReopenDialog.success);
          setCurrentPage(DEFAULT_PARAMS.page);
          setActionType(null);
          setReopenSurveyDialogOpened(false);
          setSelectedUsers([]);
          await getSurvey(dispatch, surveyId);
          await fetchReviewers(
            false,
            DEFAULT_PARAMS.page,
            filters[STATUS],
            search
          );
        });
    }
  };

  const toggleForceCompleteDialog = () =>
    setForceCompleteDialogOpened(prev => !prev);

  const handleForceComplete = () =>
    editSurvey(dispatch, survey.id, {
      [CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE]: getStartOfDay(new Date()),
    }).then(async () => {
      showSuccessMessage(translations.forceCompleteDialog.completeSuccess);
      setCurrentPage(DEFAULT_PARAMS.page);
      setForceCompleteDialogOpened(false);
      await fetchReviewers(false, DEFAULT_PARAMS.page, filters[STATUS], search);
    });

  const handleActionSelect = action => {
    const { SEND_REMINDER, REOPEN } = REVIEWER_ACTION_TYPES;

    if (action === SEND_REMINDER) {
      return toggleSendReminderDialog();
    }
    if (action === REOPEN) {
      return toggleReopenSurveyDialog();
    }
    return toggleForceCompleteDialog();
  };

  const handleSelectAll = isChecked => {
    if (isChecked) {
      setSelectedUsers(prev => getUnionOfTwoArrays(prev, reviewers.results));
    } else {
      setSelectedUsers(prev =>
        prev.filter(user => !reviewers.results.some(x => x.id === user.id))
      );
    }
  };

  const handleUserSelect = (isChecked, selectedUser) => {
    if (isChecked) {
      setSelectedUsers(prev => [...prev, { ...selectedUser }]);
    } else {
      setSelectedUsers(prev =>
        prev.filter(user => user.id !== selectedUser.id)
      );
    }
  };

  const handleSearch = text => {
    const searchTerm = trimString(text);
    setCurrentPage(DEFAULT_PARAMS.page);
    setSearch(searchTerm);
    fetchReviewers(false, DEFAULT_PARAMS.page, filters[STATUS], searchTerm);
  };

  const handleStatusChange = newStatus => {
    setCurrentPage(DEFAULT_PARAMS.page);
    setFilters({ [STATUS]: newStatus });
    fetchReviewers(false, DEFAULT_PARAMS.page, newStatus, search);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    setIsFetchingNextPage(true);
    fetchReviewers(true, nextPage, filters[STATUS], search);
  };

  const toggleAddReviewers = () => {
    setAddReviewerDialogOpened(!addReviewerDialogOpened);
    setActionType(
      !addReviewerDialogOpened ? REVIEWER_ACTION_TYPES.APPEND : null
    );
    setReviewersToAdd([]);
  };

  const handleAddReviewers = newReviewers => setReviewersToAdd(newReviewers);

  const handleSaveReviewers = () =>
    http
      .post(
        api_survey_reviewers(survey.id),
        prepareUsersForAction(reviewersToAdd, actionType)
      )
      .then(async () => {
        showSuccessMessage(translations.addReviewersDialog.success);
        setCurrentPage(DEFAULT_PARAMS.page);
        setActionType(null);
        setAddReviewerDialogOpened(false);
        setReviewersToAdd([]);
        await getSurvey(dispatch, surveyId);
        await fetchReviewers(
          false,
          DEFAULT_PARAMS.page,
          filters[STATUS],
          search
        );
      });

  const handleChangeExpiryDate = expiryDate => {
    if (!isSameDate(survey.expireAt, expiryDate)) {
      const startDate = isScheduled ? survey.scheduledFor : survey.sentAt;

      return editSurvey(dispatch, survey.id, {
        [CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE]: expiryDate,
        [CREATE_SURVEY_FIELD_NAMES.AUTO_REMINDERS]: remountAutoReminders(
          survey.autoReminders || [],
          startDate,
          survey.expireAt,
          startDate,
          expiryDate
        ),
      }).then(() => setSelectedUsers([]));
    }
  };

  const handleChangeGroup = groupId =>
    http.patch(api_survey(survey.id), { group: groupId }).then(() => {
      getSurvey(dispatch, surveyId);
      showSuccessMessage(
        survey.group
          ? translations.info.manageGroupDialog.success.edit
          : translations.info.manageGroupDialog.success.create
      );
    });

  const handleAutoShareReport = async ({ share_to }) => {
    const autoSharedReport = survey?.autoSharedReport;

    if (autoSharedReport) {
      await http
        .put(api_survey_summary_share(autoSharedReport), share_to)
        .then(() => {
          getSurvey(dispatch, surveyId);
          showSuccessMessage(translations.info.autoShareDialog.successMessage);
        });
    } else {
      const summaryData = {
        title: `${survey?.title}-Summary`,
        share_to,
        is_pre_shared: true,
        report_data: [],
      };

      await http
        .post(api_survey_summary_report(survey.id), summaryData)
        .then(() => {
          getSurvey(dispatch, surveyId);
          showSuccessMessage(translations.info.autoShareDialog.successMessage);
        });
    }
  };

  const handleEditAutoReminder = ({ auto_reminders }) => {
    const startDate = isScheduled ? survey.scheduledFor : survey.sentAt;

    return editSurvey(dispatch, survey.id, {
      [CREATE_SURVEY_FIELD_NAMES.AUTO_REMINDERS]: pruneAutoReminders(
        auto_reminders || [],
        startDate,
        survey.expireAt
      ),
    }).then(() => {
      showSuccessMessage(translations.info.autoReminderDialog.successMessage);
    });
  };

  useEffect(() => {
    try {
      getSurveyData();
    } catch (e) {
      setIsLoading(false);
    }

    return () => {
      dispatch(clearSurvey());
      dispatch(clearReviewers());
      dispatch(clearAllUsers());
      dispatch(clearAllSurveyGroups());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderSettingsHeader = () => {
    return (
      <div className={classes.header}>
        {title && (
          <>
            <div className={classes.surveyTitleWrapper}>
              <TextBoxWithTooltip variant="h1" text={title} />
              {isOneTime && (
                <SurveyIconInfoTooltip
                  className={classes.infoIcon}
                  tooltipText={translations.oneTimeSurvey}
                  isOneTimeSurvey
                />
              )}
              {isAnonymous && (
                <SurveyIconInfoTooltip
                  className={classNames(classes.anonymous, classes.infoIcon)}
                  tooltipText={translations.anonymousSurvey}
                />
              )}
            </div>
            <div className={classes.headerActions}>
              {(canViewAllSurveys || canViewSurveysCreated) &&
                survey.status === SURVEY_STATUS_TYPES.COMPLETED &&
                !survey.questionnaire && (
                  <ActionButton
                    type={ACTION_BUTTON_TYPES.DOWNLOAD}
                    onClickHandler={exportToCsv}
                  />
                )}

              {(canManageAllSurveys || canManageSurveysCreated) && (
                <ActionButton
                  type={ACTION_BUTTON_TYPES.EDIT}
                  onClickHandler={toggleSurveyTitleDialog}
                />
              )}
              {!isDraft && (
                <Link
                  className={classes.linkButton}
                  to={
                    survey.cycle
                      ? `/cycles/${survey.cycle}/reviews/${survey.id}/report`
                      : `/surveys/${survey.id}/report`
                  }
                >
                  <CustomButton
                    className={classNames(
                      classes.button,
                      classes.seeReportButton
                    )}
                    type="withTextDarkRounded"
                  >
                    {translations.seeReportButton}
                  </CustomButton>
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <PageContainer
      {...rest}
      translations={translations}
      headerProps={{
        renderTitle: renderSettingsHeader,
      }}
      organizationMentor={organizationMentor}
      auth={auth}
      accessControl={accessControl}
      organizationSettings={organizationSettings}
      navigate={navigate}
      shouldPassProps={false}
    >
      {!isLoading ? (
        <div>
          <div className={classes.divider} />
          {isPartOfCycle && (
            <Typography
              className={classes.cycleNotification}
              variant="subtitle2"
            >
              {translations.partOfCycle}
            </Typography>
          )}
          <SurveyInfo
            translations={translations.info}
            statuses={statuses}
            types={types}
            survey={survey}
            surveyGroups={allSurveyGroups}
            expiryFields={getExpiryDialogFields(sentAt || scheduledFor)}
            allUsers={employees}
            organizationUser={organizationMentor}
            canManageAllSurveys={canManageAllSurveys}
            canManageSurveysCreated={canManageSurveysCreated}
            shouldShowGroup={canViewAllSurveys && hasGroups && !isPartOfCycle}
            isPartOfCycle={isPartOfCycle}
            canAccessUserProfileCheck={canAccessUserProfileCheck}
            onGoToPersonProfile={goToProfilePage}
            onChangeGroup={handleChangeGroup}
            onEditExpiryDate={handleChangeExpiryDate}
            onAutoShareReport={handleAutoShareReport}
            onEditAutoReminder={handleEditAutoReminder}
          />
          {isAnonymous ? (
            <NotificationCard title={translations.anonymous} />
          ) : (
            <SurveyReviewers
              translations={translations.reviewers}
              reviewers={reviewers?.results}
              completionStatus={filters[STATUS]}
              selectedUsers={selectedUsers}
              actions={prepareReviewersActions(
                translations.reviewers.actions,
                canManageAllSurveys || canManageSurveysCreated,
                survey,
                !isArrayEmpty(selectedUsers)
              )}
              canManage={canManageAllSurveys || canManageSurveysCreated}
              isNpsSurvey={isNps}
              hasNextPage={hasNextPage(reviewers)}
              isNextPageLoading={isFetchingNextPage}
              isScheduled={isScheduled}
              isExpired={isDateInPast(expireAt)}
              canAccessUserProfileCheck={canAccessUserProfileCheck}
              isDisabled={isPartOfCycle || isDateInPast(expireAt)}
              isPartOfCycle={isPartOfCycle}
              canAddReviewer={hasAddReviewerButtonCheck(survey)}
              onAddReviewers={toggleAddReviewers}
              onGoToPersonProfile={goToProfilePage}
              onActionSelect={handleActionSelect}
              onStatusChange={handleStatusChange}
              onSearch={handleSearch}
              onLoadMore={handleLoadMore}
              onSelectAll={handleSelectAll}
              onUserSelect={handleUserSelect}
            />
          )}
          <AlertDialog
            translations={translations.sendReminderAllDialog}
            isOpened={sendReminderDialogOpened && isArrayEmpty(selectedUsers)}
            onClose={toggleSendReminderDialog}
            onConfirm={handleSendReminder}
          />
          <AlertDialog
            translations={translations.surveyReopenAllDialog}
            isOpened={reopenSurveyDialogOpened && isArrayEmpty(selectedUsers)}
            onClose={toggleReopenSurveyDialog}
            onConfirm={handleReopenSurvey}
          />
          <AlertDialog
            translations={translations.forceCompleteDialog}
            isOpened={forceCompleteDialogOpened}
            onClose={toggleForceCompleteDialog}
            onConfirm={handleForceComplete}
            isWarning
          />
          <SurveyActionsDialog
            translations={translations.sendReminderDialog}
            isOpened={sendReminderDialogOpened && !isArrayEmpty(selectedUsers)}
            onClose={toggleSendReminderDialog}
            onConfirm={handleSendReminder}
            {...prepareUsersToNotify(selectedUsers, status)}
          />
          <SurveyActionsDialog
            translations={translations.surveyReopenDialog}
            isOpened={reopenSurveyDialogOpened && !isArrayEmpty(selectedUsers)}
            users={selectedUsers}
            usersToNotify={selectedUsers}
            onClose={toggleReopenSurveyDialog}
            onConfirm={handleReopenSurvey}
            {...prepareUsersToNotify(selectedUsers, status, true)}
          />
          <AddReviewersDialog
            isOpened={addReviewerDialogOpened}
            translations={translations.addReviewersDialog}
            reviewers={reviewersToAdd}
            existingReviewers={getObjectToNumberArray(
              isPeerReview
                ? [...survey.reviewers, ...createdFor]
                : survey.reviewers
            )}
            users={prepareAllUsers(
              allUsers,
              survey.reviewers,
              createdFor,
              isPeerReview,
              isSelfReview,
              isNps
            )}
            usersToReview={type !== SURVEY_TYPES.SELF ? createdFor : []}
            isScheduled={isScheduled}
            onAdd={handleAddReviewers}
            onClose={toggleAddReviewers}
            onConfirm={handleSaveReviewers}
          />
          <ChooseNameDialog
            translations={
              isPartOfCycle
                ? translations.renameReviewDialog
                : translations.renameSurveyDialog
            }
            title={
              isPartOfCycle
                ? translations.renameReviewDialog.title
                : translations.renameSurveyDialog.title
            }
            description={
              isPartOfCycle
                ? translations.renameReviewDialog.description
                : translations.renameSurveyDialog.description
            }
            isOpened={isEditTitleOpened}
            initialValue={isEditTitleOpened ? title : ''}
            field={SURVEY_TITLE_FIELD}
            onCancel={toggleSurveyTitleDialog}
            onSave={onChangeSurveyTitle}
          />
        </div>
      ) : (
        <Fragment />
      )}
    </PageContainer>
  );
};

SurveySettingsPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SurveySettingsPage);
